import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
/**
 * The member's own destinations — what they came here for.
 *
 * Distinct from `/api/destinations`, which is the editorial TAXONOMY (label,
 * slug, colour). This is the parent's relationship to one: when they set it,
 * where they say it stands, what they wrote about it, and the counts that let
 * them judge it.
 *
 * ── `status` is stored, not derived ──────────────────────────────────────
 * Deliberately, and the reasoning is worth keeping: finishing two courses
 * about sleep is not the bedtime tears having stopped. A product that told a
 * parent it had judged their objective *reached* on a count of clicks would be
 * making a claim about their life it cannot support. The parent says; the
 * counts ship beside it so they say it with the evidence in view.
 *
 * ── `setOn` is stamped once ──────────────────────────────────────────────
 * Editing the note later is the objective evolving, not a new one — otherwise
 * "SET ON DAY 1" reads as day 200 because somebody fixed a typo.
 *
 * ── `counts.communityPosts` is null, and that is the schema ──────────────
 * A room carries a category and an age band and **no destination**, so nothing
 * scopes a post to one. Counting only posts on content items in the
 * destination omits every room post and reads low; counting all of a member's
 * posts answers a different question under this label. Render the two counts
 * that exist and drop the third. Giving rooms a destination is an editorial
 * change to the room taxonomy, not something to infer from a tile.
 */
export const MemberDestinationStatus = z.enum(['FRESH', 'WORKING', 'REACHED']);
export const MemberDestination = z.object({
    destinationId: z.string().uuid(),
    label: z.string(),
    colour: z.string().nullable(),
    /** A calendar day, `YYYY-MM-DD`. */
    setOn: z.string(),
    status: MemberDestinationStatus,
    /** The parent's own words. The first half of the design's narrative line. */
    note: z.string().nullable(),
    counts: z.object({
        coursesFinished: z.number().int().nonnegative(),
        anchorsSaved: z.number().int().nonnegative(),
        communityPosts: z.number().int().nonnegative().nullable(),
    }),
});
export const MemberDestinationList = z.object({
    items: z.array(MemberDestination),
    locale: z.string().optional(),
});
export function useMyDestinations(http, locale) {
    return useQuery({
        queryKey: ['me', 'destinations', locale],
        queryFn: () => http.get('/me/destinations', { schema: MemberDestinationList, locale }),
        staleTime: 5 * 60_000,
    });
}
export const SetMemberDestination = z.object({
    destinationId: z.string().uuid(),
    status: MemberDestinationStatus.optional(),
    note: z.string().max(600).nullable().optional(),
});
/**
 * One route for both gestures.
 *
 * "Add a destination" and "I've reached this" are the same write keyed on the
 * destination — the server upserts, stamping `setOn` only the first time.
 */
/*
 * Both writes answer with the WHOLE LIST, not the row that changed.
 *
 * Measured — a set returns `{ items: [...] }` carrying every destination the
 * member holds. Parsing it as a single `MemberDestination` failed at the
 * boundary, so the write succeeded on the server (201) and the client reported
 * an error and never refreshed: the card stayed on its empty state while the
 * row existed. Exactly the failure response validation is there to catch, and
 * exactly why it validates rather than trusting the shape.
 *
 * Since the list comes back anyway, it is seeded straight into the cache
 * instead of invalidating and re-fetching what we were just handed.
 */
export function useSetMyDestination(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => http.post('/me/destinations', SetMemberDestination.parse(input), {
            schema: MemberDestinationList,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'destinations'] }),
    });
}
export function useRemoveMyDestination(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (destinationId) => http.post(`/me/destinations/${destinationId}/remove`, undefined, {
            schema: MemberDestinationList,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me', 'destinations'] }),
    });
}
//# sourceMappingURL=destinations.js.map